import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography
} from "@mui/material";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import {AvailabilityService} from "crqlar-react-common/dist/services/availability/availability.service";
import {AvailabilityExceptionService} from "crqlar-react-common/dist/services/availability/availabilityException.service";
import AvailabilityChangesDialog from "crqlar-react-common/dist/components/availability/AvailabilityChangesDialog";
import {useMutation} from "react-query";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import DeleteIcon from "@mui/icons-material/Delete";
import {LoadingButton} from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
    open: boolean;
    onClose: () => void;
    blockPeriod?: AvailabilityCalculationResult;
    onDeleted: () => void;
}

const BlockPeriodDialog = ({open, onClose, blockPeriod, onDeleted}: Props) => {
    const {t} = useTranslation();
    const [changesDialogOpen, setChangesDialogOpen] = useState(false);

    // Whole availability -> for a recurring block this is the entire series (past and future)
    const deleteMut = useMutation(async () => {
        if (!blockPeriod?.availabilityId) return;
        await AvailabilityService.deleteAvailability({id: [blockPeriod.availabilityId]});
    });

    // Only this occurrence: same as the planner's "this event only" delete -> a deleted exception
    const deleteOccurrenceMut = useMutation(async () => {
        if (!blockPeriod?.availabilityId) return;
        const exception = {
            availabilityId: blockPeriod.availabilityId,
            originalStartTime: blockPeriod.occurrenceStartTime!,
            newStartTime: blockPeriod.startTime,
            newEndTime: blockPeriod.endTime,
            name: blockPeriod.name,
            color: blockPeriod.color,
            capacityOverride: blockPeriod.capacityOverride,
            type: blockPeriod.type,
            deleted: true,
        };
        if (blockPeriod.exceptionId) {
            await AvailabilityExceptionService.update({...exception, id: blockPeriod.exceptionId});
        } else {
            await AvailabilityExceptionService.create(exception);
        }
    });

    const handleDelete = async () => {
        if (blockPeriod?.rrule) {
            setChangesDialogOpen(true);
            return;
        }
        await deleteMut.mutateAsync();
        onDeleted();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {t('Block period')}
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={1.5} sx={{mt: 1}}>
                        {blockPeriod?.name && (
                            <Typography>
                                <strong>{t('Block name')}:</strong> {blockPeriod.name}
                            </Typography>
                        )}
                        <Typography>
                            <strong>{t('Start Date & Time')}:</strong>{' '}
                            {blockPeriod?.startTime ? formatDateDMYHM(new Date(blockPeriod.startTime)) : '-'}
                        </Typography>
                        <Typography>
                            <strong>{t('End Date & Time')}:</strong>{' '}
                            {blockPeriod?.endTime ? formatDateDMYHM(new Date(blockPeriod.endTime)) : '-'}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">{t('Cancel')}</Button>
                    <LoadingButton
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        startIcon={<DeleteIcon/>}
                        loading={deleteMut.isLoading || deleteOccurrenceMut.isLoading}
                    >
                        {t('Delete block period')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <AvailabilityChangesDialog
                open={changesDialogOpen}
                onClose={() => setChangesDialogOpen(false)}
                onPick={async (option) => {
                    setChangesDialogOpen(false);
                    if (option === 'all') {
                        await deleteMut.mutateAsync();
                    } else {
                        await deleteOccurrenceMut.mutateAsync();
                    }
                    onDeleted();
                }}
                changeOptions={['current', 'all']}
            />
        </>
    );
};

export default BlockPeriodDialog;
